import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { COMMON_TIMEZONES, userBrowserTimezone } from "@/lib/time";
import STT_LANGUAGES from "@/lib/sttLanguages";
import StripeConnectPanel from "@/components/StripeConnectPanel";
import { BillingPeriodToggle, PricingCard, FreeTierBanner } from "@/pages/Pricing";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const { currentWorkspace, fetchWorkspaces } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab") || "profile";
  const [tab, setTab] = useState(initial);

  return (
    <div className="flex flex-col gap-6 max-w-4xl" data-testid="settings-page">
      <header>
        <p className="bloq-eyebrow">Settings</p>
        <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">Make Bloq yours.</h1>
      </header>
      <Tabs value={tab} onValueChange={(v) => { setTab(v); setParams({ tab: v }); }}>
        <TabsList>
          <TabsTrigger value="profile" data-testid="settings-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="workspace" data-testid="settings-tab-workspace">Workspace</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="settings-tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="email" data-testid="settings-tab-email">Email</TabsTrigger>
          <TabsTrigger value="billing" data-testid="settings-tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security">Security</TabsTrigger>
        </TabsList>
        <TabsContent value="profile" className="mt-5">
          <ProfileTab user={user} refreshUser={refreshUser} />
        </TabsContent>
        <TabsContent value="workspace" className="mt-5">
          <WorkspaceTab workspace={currentWorkspace} fetchWorkspaces={fetchWorkspaces} />
        </TabsContent>
        <TabsContent value="integrations" className="mt-5">
          <IntegrationsTab />
        </TabsContent>
        <TabsContent value="email" className="mt-5">
          <EmailRedirectTab />
        </TabsContent>
        <TabsContent value="billing" className="mt-5">
          <BillingTab />
        </TabsContent>
        <TabsContent value="security" className="mt-5">
          <SecurityTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProfileTab({ user, refreshUser }) {
  const { currentWorkspace, fetchWorkspaces } = useWorkspace();
  const wsAdmin = currentWorkspace?.my_role === "owner" || currentWorkspace?.my_role === "admin";
  const [firstName, setFirstName] = useState(user?.first_name || (user?.name || "").split(" ")[0] || "");
  const [lastName, setLastName] = useState(user?.last_name || (user?.name || "").split(" ").slice(1).join(" ") || "");
  const [tz, setTz] = useState(user?.timezone || userBrowserTimezone());
  const [wsTz, setWsTz] = useState(currentWorkspace?.timezone || "UTC");
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    setFirstName(user?.first_name || (user?.name || "").split(" ")[0] || "");
    setLastName(user?.last_name || (user?.name || "").split(" ").slice(1).join(" ") || "");
    setTz(user?.timezone || userBrowserTimezone());
  }, [user]);
  useEffect(() => {
    setWsTz(currentWorkspace?.timezone || "UTC");
  }, [currentWorkspace]);
  const save = async () => {
    if (!firstName.trim()) { toast.error("First name is required"); return; }
    // Personal profile (incl. personal timezone) — always attempted first.
    // Saved via PATCH /auth/me which writes the existing `users.timezone`
    // field; no schema change.
    let personalOk = false;
    try {
      await api.patch(`/auth/me`, { first_name: firstName.trim(), last_name: lastName.trim(), timezone: tz });
      personalOk = true;
    } catch { /* fall through to combined error toast */ }

    // Workspace default timezone — partial PATCH (only `timezone` field).
    // Backend filters to allowed_fields, so other workspace fields stay
    // untouched. Only fired when the value actually changed AND the user
    // has admin/owner permissions on the workspace.
    let wsAttempted = false;
    let wsOk = false;
    const currentWsTz = currentWorkspace?.timezone || "UTC";
    if (currentWorkspace && wsAdmin && wsTz !== currentWsTz) {
      wsAttempted = true;
      try {
        await api.patch(`/workspaces/${currentWorkspace.workspace_id}`, { timezone: wsTz });
        wsOk = true;
      } catch { /* surfaced below */ }
    }

    // Refresh local state regardless of partial failure so the UI matches
    // server truth.
    try { await refreshUser(); } catch { /* ignore */ }
    if (wsAttempted) {
      try { await fetchWorkspaces(); } catch { /* ignore */ }
    }

    if (personalOk && (!wsAttempted || wsOk)) {
      toast.success(wsAttempted ? "Profile and workspace timezone updated" : "Profile updated");
    } else if (personalOk && wsAttempted && !wsOk) {
      toast.warning("Personal settings saved, but workspace timezone could not be updated. You may not have admin permission.");
    } else {
      toast.error("Could not save");
    }
  };
  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/auth/me/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Avatar updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="bloq-card p-6 flex flex-col gap-4">
      <p className="bloq-headline text-xl text-[#005e7a]">Profile</p>
      <div className="flex items-center gap-4">
        <div
          className="w-20 h-20 rounded-md overflow-hidden flex items-center justify-center text-white text-2xl font-semibold shadow-sm flex-shrink-0"
          style={{ background: "#005e7a" }}
          data-testid="profile-avatar-preview"
        >
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            initials(user?.name)
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="bloq-btn-secondary inline-flex items-center gap-2 cursor-pointer text-xs w-fit" data-testid="profile-upload-avatar-label">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => uploadAvatar(e.target.files?.[0])}
              data-testid="profile-upload-avatar-input"
            />
            {uploading ? "Uploading…" : "Upload square avatar"}
          </label>
          <p className="text-xs text-[#82828c]">PNG or JPG, square crops best. Max 5MB.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">First name</label>
          <input className="bloq-input mt-1" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="profile-firstname-input" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Last name</label>
          <input className="bloq-input mt-1" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="profile-lastname-input" />
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Email</label>
        <input className="bloq-input mt-1" value={user?.email || ""} disabled data-testid="profile-email-input" />
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Personal Time Zone</label>
        <Select value={tz} onValueChange={setTz}>
          <SelectTrigger className="mt-1" data-testid="profile-tz-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-60">
            {COMMON_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-xs text-[#82828c] mt-1">Used for your own meeting times, reminders, and personal scheduling display.</p>
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Workspace Default Time Zone</label>
        <Select value={wsTz} onValueChange={setWsTz} disabled={!currentWorkspace || !wsAdmin}>
          <SelectTrigger className="mt-1" data-testid="profile-workspace-tz-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-60">
            {COMMON_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-xs text-[#82828c] mt-1">
          {!currentWorkspace
            ? "Loading workspace…"
            : !wsAdmin
              ? "Only workspace admins can change this."
              : "Used as the default timezone for workspace scheduling, bookings, and team-related settings."}
        </p>
      </div>
      <p className="text-xs text-[#82828c] -mt-1">
        Bloq uses these timezone settings for scheduling, bookings, meeting invitations, reminders, and calendar-related features.
      </p>
      <button onClick={save} className="bloq-btn-primary self-start inline-flex items-center gap-2" data-testid="profile-save-button">
        <Save className="w-4 h-4" /> Save changes
      </button>
    </div>
  );
}

function WorkspaceTab({ workspace, fetchWorkspaces }) {
  const [name, setName] = useState(workspace?.name || "");
  const [aiEnabled, setAiEnabled] = useState(workspace?.ai_features_enabled !== false);
  const [sttLang, setSttLang] = useState(workspace?.default_transcription_language || "en-US");
  const [sttAuto, setSttAuto] = useState(workspace?.transcription_auto_start === true);
  const [sttProviderConfigured, setSttProviderConfigured] = useState(false);
  const [aiStatus, setAiStatus] = useState(null);
  useEffect(() => {
    setName(workspace?.name || "");
    setAiEnabled(workspace?.ai_features_enabled !== false);
    setSttLang(workspace?.default_transcription_language || "en-US");
    setSttAuto(workspace?.transcription_auto_start === true);
    if (workspace?.workspace_id) {
      api
        .get(`/workspaces/${workspace.workspace_id}/ai-status`)
        .then((r) => setAiStatus(r.data))
        .catch(() => setAiStatus(null));
    }
    api.get("/transcription/provider").then((r) => {
      setSttProviderConfigured(!!r.data?.configured);
    }).catch(() => setSttProviderConfigured(false));
  }, [workspace]);
  const save = async () => {
    if (!workspace) return;
    try {
      // NOTE: workspace.timezone is intentionally NOT included here.
      // It moved to Account Settings → Profile tab (v2.78) so users see
      // both timezone fields in one place. Partial PATCH means leaving
      // the field out keeps whatever value is already on the workspace
      // doc — no clobber risk.
      await api.patch(`/workspaces/${workspace.workspace_id}`, {
        name,
        ai_features_enabled: aiEnabled,
        default_transcription_language: sttLang,
        transcription_auto_start: sttAuto,
      });
      await fetchWorkspaces();
      toast.success("Workspace updated");
    } catch { toast.error("Could not save"); }
  };
  return (
    <div className="bloq-card p-6 flex flex-col gap-4">
      <p className="bloq-headline text-xl text-[#005e7a]">Workspace</p>
      <div>
        <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Workspace name</label>
        <input className="bloq-input mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="workspace-name-input" />
      </div>

      <div className="bg-[#f5efe6] rounded-lg p-4 flex flex-col gap-2" data-testid="ai-features-toggle">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Allow AI task suggestions from meeting content</p>
            <p className="text-xs text-[#82828c] mt-0.5">
              When enabled, Bloq can send meeting notes, chat, and AI summary text to the AI service to suggest action items.
              Disable this if your workspace has strict data residency rules.
            </p>
            {aiStatus && !aiStatus.key_configured && (
              <p className="text-xs text-[#b45309] mt-1.5">
                AI key not configured on the server — toggle is ineffective until an admin adds EMERGENT_LLM_KEY.
              </p>
            )}
          </div>
          <button
            onClick={() => setAiEnabled((v) => !v)}
            role="switch"
            aria-checked={aiEnabled}
            className="relative w-11 h-6 rounded-full transition-colors flex-shrink-0"
            style={{ background: aiEnabled ? "#005e7a" : "#cccccc" }}
            data-testid="ai-features-toggle-switch"
          >
            <span
              className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
              style={{ transform: aiEnabled ? "translateX(20px)" : "translateX(0)" }}
            />
          </button>
        </div>
      </div>

      <div className="bg-[#f5efe6] rounded-lg p-4 flex flex-col gap-3" data-testid="ws-transcription-defaults">
        <p className="text-sm font-medium">Meeting transcription defaults</p>
        {!sttProviderConfigured && (
          <p className="text-xs text-[#b45309]">
            Agora Real-Time STT isn't configured on this server. These settings will only take effect once the integration is enabled.
          </p>
        )}
        <div>
          <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Default language</label>
          <Select value={sttLang} onValueChange={setSttLang}>
            <SelectTrigger className="mt-1" data-testid="ws-stt-language"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-60">
              {STT_LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-[#82828c] mt-1">Used as the preselected language in every meeting's transcription panel.</p>
        </div>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Auto-start full meeting transcription</p>
            <p className="text-xs text-[#82828c] mt-0.5">
              When enabled, the host's browser will automatically start Agora full-meeting transcription a few seconds after joining. Hosts can still stop it from inside the meeting.
            </p>
          </div>
          <button
            onClick={() => setSttAuto((v) => !v)}
            role="switch"
            aria-checked={sttAuto}
            className="relative w-11 h-6 rounded-full transition-colors flex-shrink-0"
            style={{ background: sttAuto ? "#005e7a" : "#cccccc" }}
            data-testid="ws-stt-auto-toggle"
          >
            <span
              className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
              style={{ transform: sttAuto ? "translateX(20px)" : "translateX(0)" }}
            />
          </button>
        </div>
      </div>

      <button onClick={save} className="bloq-btn-primary self-start inline-flex items-center gap-2" data-testid="workspace-save-button">
        <Save className="w-4 h-4" /> Save
      </button>
    </div>
  );
}

function IntegrationsTab() {
  const { currentWorkspace } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    if (!currentWorkspace) return;
    try {
      const r = await api.get(`/integrations/status`, { params: { workspace_id: currentWorkspace.workspace_id } });
      setStatus(r.data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    load();
    // Show toast if redirected back from OAuth callback
    const flag = params.get("integrations");
    if (flag?.startsWith("connected:")) {
      const provider = flag.split(":")[1];
      toast.success(`Connected ${provider.replace("_", " ")}`);
      params.delete("integrations");
      setParams(params);
    }
    // eslint-disable-next-line
  }, [currentWorkspace]);

  const connect = async (provider) => {
    if (!currentWorkspace) return;
    setBusy(provider);
    try {
      const r = await api.get(`/integrations/${provider === "google_calendar" ? "google-calendar" : "outlook"}/start`, {
        params: { workspace_id: currentWorkspace.workspace_id },
      });
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start OAuth flow");
      setBusy(null);
    }
  };

  const disconnect = async (provider) => {
    if (!currentWorkspace) return;
    try {
      await api.delete(`/integrations/${provider === "google_calendar" ? "google-calendar" : "outlook"}`, {
        params: { workspace_id: currentWorkspace.workspace_id },
      });
      toast.success("Disconnected");
      load();
    } catch { toast.error("Could not disconnect"); }
  };

  const renderProvider = (key, label, desc) => {
    const meta = status?.[key];
    const connected = meta?.status === "connected";
    const configurationRequired = meta?.configuration_required;
    return (
      <div key={key} className="flex items-center justify-between gap-3 bg-[#f5efe6] p-4 rounded-lg" data-testid={`integration-${key}`}>
        <div className="min-w-0">
          <p className="font-medium">{label}</p>
          <p className="text-xs text-[#82828c]">{desc}</p>
          {connected && (
            <p className="text-xs text-[#005e7a] mt-1 font-medium">Connected as {meta?.connected_email || "unknown"}</p>
          )}
          {configurationRequired && !connected && (
            <p className="text-xs text-[#b45309] mt-1">Configuration required — admin must add OAuth credentials to the server before users can connect.</p>
          )}
        </div>
        <div className="flex-shrink-0">
          {connected ? (
            <button onClick={() => disconnect(key)} className="bloq-btn-secondary text-xs px-3 py-1.5" data-testid={`disconnect-${key}`}>Disconnect</button>
          ) : (
            <button
              onClick={() => connect(key)}
              disabled={configurationRequired || busy === key}
              className={`text-xs px-3 py-1.5 rounded-md font-medium ${configurationRequired ? "bg-[#e5e5e5] text-[#82828c] cursor-not-allowed" : "bloq-btn-primary"}`}
              data-testid={`connect-${key}`}
            >
              {busy === key ? "Redirecting…" : (configurationRequired ? "Setup required" : "Connect")}
            </button>
          )}
        </div>
      </div>
    );
  };

  const otherIntegrations = [
    { name: "iCal export", status: "Available", desc: "Download .ics for any meeting." },
    { name: "Resend (email)", status: "Configuration required", desc: "Outbound email delivery (invites, reminders, follow-ups)." },
    { name: "Slack notifications", status: "Coming soon", desc: "Ping a channel when a meeting starts or a booking lands." },
  ];

  return (
    <div className="bloq-card p-6 flex flex-col gap-4">
      <p className="bloq-headline text-xl text-[#005e7a]">Integrations</p>
      <p className="text-sm text-[#4a4a52]">Connect Bloq to the tools you already use. Booking availability respects connected calendars.</p>
      <div className="flex flex-col gap-3 mt-2">
        {renderProvider("google_calendar", "Google Calendar", "Two-way sync your meetings + use external events as availability conflicts.")}
        {renderProvider("microsoft_outlook", "Microsoft Outlook / Office 365", "Two-way calendar sync via Microsoft Graph API.")}
        <StripeConnectPanel workspaceId={currentWorkspace?.workspace_id} />
        <div className="flex items-center justify-between gap-3 bg-[#f5efe6] p-4 rounded-lg opacity-70" data-testid="integration-icloud">
          <div>
            <p className="font-medium">iCloud Calendar</p>
            <p className="text-xs text-[#82828c]">Apple iCloud uses CalDAV + app-specific passwords. Not yet implemented.</p>
          </div>
          <span className="bloq-pill" style={{ background: "rgba(0,94,122,0.1)", color: "#005e7a" }}>Coming soon</span>
        </div>
        {otherIntegrations.map((i) => (
          <div key={i.name} className="flex items-center justify-between gap-3 bg-[#f5efe6] p-4 rounded-lg" data-testid={`integration-${i.name.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div>
              <p className="font-medium">{i.name}</p>
              <p className="text-xs text-[#82828c]">{i.desc}</p>
            </div>
            <span className="bloq-pill" style={{ background: i.status === "Available" ? "#005e7a" : "rgba(0,94,122,0.1)", color: i.status === "Available" ? "#fff" : "#005e7a" }}>
              {i.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BillingTab() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default to annual — biggest savings, matches the public /pricing page.
  const [billingPeriod, setBillingPeriod] = useState("annual");

  useEffect(() => {
    let alive = true;
    api.get("/marketing/pricing")
      .then((r) => alive && setTiers(r.data || []))
      .catch(() => alive && setTiers([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const paidTiers = tiers.filter((t) => t.tier_id !== "free" && t.price_monthly !== 0);
  const freeTier = tiers.find((t) => t.tier_id === "free" || t.price_monthly === 0);

  return (
    <div className="flex flex-col gap-5">
      <div className="bloq-card p-6 flex flex-col gap-3" data-testid="billing-tab">
        <p className="bloq-headline text-xl text-[#005e7a]">Billing</p>
        <p className="text-sm text-[#4a4a52]">
          Bloq is in beta — you're on the free plan. Stripe billing rolls out shortly. We'll notify you
          before any plan is charged.
        </p>
        <div className="bg-[#f5efe6] p-4 rounded-lg flex items-center justify-between">
          <div>
            <p className="font-medium">Current plan</p>
            <p className="text-xs text-[#82828c]">Free · individual usage</p>
          </div>
          <span className="bloq-pill">Beta access</span>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-sm text-[#82828c]">Loading plans…</p>
      ) : tiers.length === 0 ? (
        <p className="text-center text-sm text-[#82828c]">Plan details are temporarily unavailable.</p>
      ) : (
        <>
          <BillingPeriodToggle value={billingPeriod} onChange={setBillingPeriod} />
          <div className="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-2 gap-5" data-testid="billing-tiers">
            {paidTiers.map((t) => (
              <PricingCard key={t.tier_id} tier={t} billingPeriod={billingPeriod} />
            ))}
          </div>
          {freeTier && <FreeTierBanner tier={freeTier} />}
          <p className="text-xs text-[#82828c] text-center mt-2">
            Pricing subject to change. <Link to="/pricing" className="underline">See full pricing details</Link>.
          </p>
        </>
      )}
    </div>
  );
}

function SecurityTab() {
  return (
    <div className="bloq-card p-6 flex flex-col gap-4">
      <p className="bloq-headline text-xl text-[#005e7a]">Security</p>
      <ul className="text-sm text-[#4a4a52] flex flex-col gap-2.5">
        <li>• Sessions are stored in httpOnly cookies + JWT.</li>
        <li>• Agora video tokens are issued server-side and scoped to a single meeting.</li>
        <li>• Public meeting IDs are 12-char URL-safe random tokens — not guessable.</li>
        <li>• Your workspace data is isolated from other workspaces by backend authorization checks.</li>
      </ul>
      <p className="text-sm text-[#4a4a52]">
        Need to delete your account or export your data? Email <strong>privacy@bloq.video</strong>.
      </p>
    </div>
  );
}

function EmailRedirectTab() {
  return (
    <div className="bloq-card p-6 flex flex-col gap-4" data-testid="email-redirect-tab">
      <p className="bloq-headline text-xl text-[#005e7a]">Email notifications</p>
      <p className="text-sm text-[#4a4a52]">
        Configure provider, branding, templates, triggers, preferences, logs, and audience sync in the dedicated email admin.
      </p>
      <Link to="/app/settings/email" className="bloq-btn-primary inline-flex items-center gap-2 w-fit" data-testid="open-email-admin">
        Open Email Settings →
      </Link>
    </div>
  );
}

